/**
 * AP-22008 — resolves a legacy repo-root `dist/…` asset path onto the prefix the
 * Enterprise image actually serves it from.
 *
 * These files were served at `/static/dist/…` by the retired `platform-ui`
 * image, whose `angular_dist` volume was mounted at that prefix. AP-19874
 * retired that image, and MR !7428 made the Enterprise image the transport for
 * the statics that survived — but it publishes them inside
 * `/opt/attackiq/enterprise`, which the cloud static export and the on-prem
 * `enterprise_dist` volume both expose at `/static/dist/enterprise/`. Nothing
 * has served the bare `/static/dist/…` paths since, so every one of them 404s.
 *
 * Publishing inside `/opt/attackiq/enterprise` is deliberate rather than
 * incidental: it is what lets the tree reach both cloud and on-prem with no new
 * volume, deploy hook or object-storage prefix, and it keeps the frozen
 * `enterprise-preauth-v1` operator contract untouched. Moving the assets back to
 * the bare prefix would need a coordinated onprem-operator change instead. See
 * `packages/preauth-assets/README.md`.
 *
 * The rewrite lives here rather than at each call site because the literal
 * `dist/…` strings are not all in code: `dist/how_to_guides.json` ships
 * `thumbnail_url` values in the same form, and they reach `staticPath` the same
 * way a hard-coded path does.
 *
 * Apply it through `./staticPath`, which is the one place `STATIC_URL` and a
 * `dist/…` path are concatenated. Call sites that build that URL by hand bypass
 * the rewrite and keep 404ing — that is exactly how `global-property-icon.svg`
 * survived the first pass at this fix.
 */

/** Prefix the retired `angular_dist` volume was mounted at. */
const LEGACY_DIST_PREFIX = 'dist/';

/**
 * Prefix the Enterprise image publishes, set by the `COPY` in
 * `apps/enterprise/docker/Dockerfile` that lands the tree in
 * `/opt/attackiq/enterprise/`.
 */
const ENTERPRISE_DIST_PREFIX = 'dist/enterprise/';

/**
 * Rewrites a `dist/…` path to `dist/enterprise/…`. Anything else — an absolute
 * URL, a `/downloads/…` path, an already-resolved path, `undefined` — is
 * returned untouched, so this is safe to apply at a call site that handles a
 * mix of sources.
 */
export const resolveLegacyStaticPath = (url: string | undefined): string | undefined => {
  if (url === undefined) return undefined;
  if (url.startsWith(ENTERPRISE_DIST_PREFIX)) return url;
  if (!url.startsWith(LEGACY_DIST_PREFIX)) return url;

  return `${ENTERPRISE_DIST_PREFIX}${url.slice(LEGACY_DIST_PREFIX.length)}`;
};
